/**
 * 早弁先生。
 *
 * 授業中、先生が黒板を向いているすきに弁当を食べる。画面は3つ。
 *
 *   1. 食材を選ぶ … うまいものほど ひと口が大きいが、音が出て、教科書からはみ出る
 *   2. こっそり食べる … **長押しで食べる。指を離すと隠す。**
 *
 *      見えるのは**「ひと口ゲージ」1本だけ**。満タンになるとその ひと口を食べきって、
 *      グラムが増える。指を離すと、ゲージはゆっくり戻っていく。
 *
 *      **危なさは全部見えないところにある。**
 *      ・音（見えない）… 食べているあいだ上がり、離すと速く下がる
 *      ・疑い（見えない）… 先生の頭の中。音が大きいほど速くたまる。
 *        たまると「ん？」、満タンで振り向く。手を離すと下がって「気のせいか…」
 *      ・隠しきるまでの間（見えない）… 手を離してもしばらくは丸見え。うまいものほど長い
 *
 *      **先生がいつ振り向くかはゲージで分からない。** 分かってしまうと
 *      ゲージを合わせるだけの遊びになる。読めるのは「ん？」という反応だけ。
 *   3. 結果 … 食べた量（g）がそのまま点。見つかっても、そこまでのぶんは残る
 *
 * 絵は `public/hayaben/*.webp`（`scripts/build_hayaben_assets.mjs` が作る）。
 */
import { openXPostOnWeb } from './x-post.js';
import './pages.css';

const $ = <T extends HTMLElement = HTMLElement>(id: string): T => document.getElementById(id) as T;
const esc = (s: string): string =>
  s.replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' })[c] as string);

/* ============================ 食材 ============================ */

interface Food {
  id: string;
  name: string;
  /** 1〜10。大きいほど うまくて危ない。並び順にも使う */
  level: number;
  /** ひと口のグラム数。そのまま点になる */
  g: number;
  /** ひと口を食べきるまで（ミリ秒）＝ゲージが満タンになるまで */
  ms: number;
  /** 音の大きさ 1〜10。**見せない。** 食べているあいだ、疑いのたまる速さになる */
  sound: number;
  /** はみ出し 1〜10。**見せない。** 机に出ているあいだ じわじわ疑われ、
      手を離してから隠しきるまでの時間にもなる */
  vis: number;
}

/**
 * 弁当の中身。**レベル1〜10。**
 * g・音・はみ出しは `assets/hayaben-assets/food-levels.json`（＝ FOOD-LEVELS.md）のまま。
 *
 * 足すときは、絵を3枚（`foods/`・`foods/on-table/`・`foods/in-hand/`）置いて
 * `food-levels.json` に1つ足し、`pnpm hayaben:assets` を走らせて、ここに1行足す。
 *
 * `ms` だけはゲームの都合で決めている（レベル1の 540ms からレベル10の 720ms まで）。
 * **ここをレベルなりに伸ばしてはいけない。** 伸ばすと、うるさい食材は
 * 「ん？」が来るまでに ひと口も食べ終われず、選ぶ意味がなくなる（一度やらかした）。
 * ほぼ横ばいにしてあるので、**レベルが高いほど1秒あたりのグラムが大きい**＝
 * 危ないぶん もうかる。
 */
const FOODS: readonly Food[] = [
  { id: 'onigiri', name: 'おにぎり', level: 1, g: 12, ms: 540, sound: 1, vis: 1 },
  { id: 'tamagoyaki', name: '卵焼き', level: 2, g: 18, ms: 560, sound: 1, vis: 2 },
  { id: 'karaage', name: '唐揚げ', level: 3, g: 24, ms: 580, sound: 3, vis: 3 },
  { id: 'baguette', name: 'フランスパン', level: 4, g: 30, ms: 600, sound: 5, vis: 5 },
  { id: 'ramen', name: 'ラーメン', level: 5, g: 40, ms: 620, sound: 6, vis: 6 },
  { id: 'pizza', name: 'ピザ', level: 6, g: 50, ms: 640, sound: 3, vis: 7 },
  { id: 'sashimi-boat', name: '舟盛り', level: 7, g: 60, ms: 660, sound: 3, vis: 9 },
  { id: 'king-crab', name: 'タラバガニ', level: 8, g: 75, ms: 680, sound: 8, vis: 9 },
  { id: 'hotpot', name: '寄せ鍋', level: 9, g: 90, ms: 700, sound: 7, vis: 10 },
  { id: 'yakiniku', name: '卓上焼肉', level: 10, g: 110, ms: 720, sound: 10, vis: 10 },
];

/** 食べているあいだ、音が1秒で何%たまるか */
const noiseOf = (f: Food): number => 8 + f.sound * 8.3;
/** 手を離してから隠しきるまで（ミリ秒）。この間に振り向かれたらアウト */
const hideOf = (f: Food): number => 60 + f.vis * 72;

/** レベルから、見出しに出す言い方 */
const tierOf = (lv: number): string => (lv <= 3 ? '初級' : lv <= 7 ? '中級' : '上級');
/** 1〜100 を ●5つに */
const dots = (pct: number): string => {
  const n = Math.max(1, Math.min(5, Math.round(pct / 20)));
  return `<i class="on">${'●'.repeat(n)}</i><i>${'●'.repeat(5 - n)}</i>`;
};

/* ============================ 決めごと ============================ */

/** 1回の長さ（秒） */
const ROUND = 60;
/** 指を離しているとき、音が1秒で何%下がるか */
const COOL_NOISE = 42;
/** 指を離したとき、ひと口ゲージが戻る速さ（たまる速さの何倍か） */
const BITE_DRAIN = 0.5;
const BEST_KEY = 'moji-tower:hayaben:best';

/* ---- 先生の「疑い」（見えない） ----
   **先生は時間では振り向かない。音でしか振り向かない。**

   いちばん大事な決めごと:
   **「ん？」が出たところで手を離せば、かならず「気のせいか…」に戻る。**
   そこから先に進むのは、**「ん？」のあとも食べ続けたときだけ**。
   そうしないと、うるさい食材が「気づかれた時点で詰み」になって、遊びにならない。 */

/** 食べているあいだ、音が満タンなら疑いが1秒でどれだけ上がるか（音が半分なら半分） */
const SUSPECT_GAIN = 70;
/** はみ出しが10のとき、**食べているあいだ**さらに1秒でどれだけ上がるか。
   舟盛りや鍋は、音が小さくても机の上が目立つので早く気づかれる */
const VIS_GAIN = 26;
/** 手を止めているあいだ、疑いが1秒でどれだけ下がるか */
const SUSPECT_COOL = 35;
/** 「ん？」が出ているあいだ、手を止めたときに下がる速さ。
 **ここを速くしておくことで「止めれば helps」が必ず成り立つ** */
const HINT_COOL = 95;
/** 音が初めて満タンになった瞬間の「ガリッ」。疑いが一気に上がる */
const SUSPECT_SPIKE = 12;
/** ここまでたまると「ん？」。毎回すこしずらして、覚えゲーにしない */
const HINT_AT = [44, 56] as const;
/** 「ん？」が出てから、先生が振り向くと決めるまで（ミリ秒）。
   **この間に手を離せば助かる。** どの食材でも同じ長さにしてある。
   うまいものが難しいのは「ん？」が早く来るからで、猶予が短いからではない */
const GRACE_MS = 700;
/** 振り向くと決めてから、実際に振り向くまで（ミリ秒）。
   **「隠しきるまでの時間」が効くのはこの一拍。**
   ここで慌てて離しても、しまうのが遅い食材は間に合わない */
const TURN_DELAY = 260;
/** 「ん？」のあと、ここまで下がったら「気のせいか…」に戻る */
const HINT_RELEASE = 14;
/** 振り向いているあいだの長さ（ミリ秒） */
const LOOK_MS = [900, 1400] as const;
/** 「気のせいか…」を出しておく長さ（ミリ秒） */
const RELIEF_MS = 1300;

/** 食べた量から、終わりに出す呼び名 */
function rankOf(g: number): string {
  if (g >= 700) return '伝説の早弁王';
  if (g >= 480) return '早弁の達人';
  if (g >= 300) return 'ベテラン';
  if (g >= 150) return '常習犯';
  if (g >= 60) return 'かけだし';
  return '見習い';
}

/* ============================ 状態 ============================ */

type Screen = 'pick' | 'play' | 'done';
/** 先生のようす */
type Teacher = 'board' | 'hint' | 'look';
/** 見つかった理由 */
type Caught = null | 'noise' | 'slow' | 'seen';

let screen: Screen = 'pick';
let food: Food = FOODS[0] as Food;
let eaten = 0;
/** いまのひと口の進み（0〜1）。**唯一ユーザーに見せるゲージ** */
let bite = 0;
/** 音（0〜100）。見せない */
let noise = 0;
/** 隠しきるまでの終わりの時刻。ここまでは手を止めていても丸見え */
let hideUntil = 0;
let teacher: Teacher = 'board';
/** 先生の疑い（0〜100）。**画面には出さない** */
let suspect = 0;
/** 今回「ん？」になるしきい値。毎回すこしずらす */
let hintAt = 56;
/** 「ん？」が出た時刻。ここから GRACE_MS のあいだに手を離せば助かる */
let hintAt0 = 0;
/** 先生が「振り向く」と決めたか。ここから先は手を離しても止まらない */
let committed = false;
/** 振り向く時刻（決めたときに入る） */
let turnAt = 0;
/** 振り向きが終わる時刻 */
let lookUntil = 0;
/** 「気のせいか…」を出しておく終わりの時刻 */
let reliefUntil = 0;
/** 音が満タンになって「ガリッ」を出したか（1回だけ） */
let spiked = false;
/** 長押ししているか */
let eating = false;
let endsAt = 0;
let best = 0;
let lastT = 0;
let shareText = '';
/** テスト用。真にすると疑いがたまらない */
let frozen = false;

const game = $('game');
const rand = (a: number, b: number): number => a + Math.random() * (b - a);
const now = (): number => performance.now();

/* ============================ 画面 ============================ */

/**
 * 机置き・手持ちの絵を、選んだ食材のものに差し替える。
 * **選んだ1つぶんしか読み込まない**（10食材ぶん先に落とすと重い）。
 */
function setFoodImages(): void {
  $<HTMLImageElement>('imgTable').src = `/hayaben/table-${food.id}.webp`;
  $<HTMLImageElement>('imgHand').src = `/hayaben/hand-${food.id}.webp`;
  // 絵はどれもキャンバスいっぱいに描いてあるので、おにぎりも焼肉も同じ大きさになってしまう。
  // レベルで縮めて、机の上の大きさの差を出す
  game.style.setProperty('--fscale', String(0.62 + food.level * 0.045));
}

function renderPick(): void {
  $('pickList').innerHTML = [...FOODS]
    .sort((a, b) => a.level - b.level)
    .map(
      (f) =>
        `<button type="button" class="pick" data-id="${f.id}" aria-pressed="${f.id === food.id}">` +
        `<img src="/hayaben/food-${f.id}.webp" alt="" width="200" height="200" loading="lazy" />` +
        `<span class="pname"><em class="tier t${f.level <= 3 ? 1 : f.level <= 7 ? 2 : 3}">${tierOf(f.level)}</em>` +
        `<b>${esc(f.name)}</b><small>${f.g}g<i> / ひと口</i></small></span>` +
        `<span class="pmeters t${f.level <= 3 ? 1 : f.level <= 7 ? 2 : 3}">` +
        `<span><small>音</small>${dots(f.sound * 10)}</span>` +
        `<span><small>はみ出し</small>${dots(f.vis * 10)}</span></span>` +
        `<span class="pcheck" aria-hidden="true">✓</span>` +
        `</button>`,
    )
    .join('');
  const bb = $('pickBest');
  bb.hidden = best <= 0;
  bb.querySelector('b')!.textContent = String(best);
}

function renderPlay(): void {
  $('grams').textContent = String(eaten);
  const left = Math.max(0, Math.ceil((endsAt - now()) / 1000));
  $('clock').textContent =
    `${String(Math.floor(left / 60)).padStart(2, '0')}:${String(left % 60).padStart(2, '0')}`;
  $('biteBar').style.width = `${bite * 100}%`;
  $('biteName').textContent = food.name;
  $('biteG').textContent = `+${food.g}g`;
  game.dataset['teacher'] = teacher;
  game.dataset['eating'] = eating ? 'on' : 'off';
  $('state').textContent =
    teacher === 'look'
      ? 'ふりむいた！'
      : teacher === 'hint'
        ? 'ん？'
        : now() < reliefUntil
          ? '気のせいか…'
          : '先生は板書中';
}

function show(s: Screen): void {
  screen = s;
  game.dataset['screen'] = s;
}

/* ============================ 遊び ============================ */

/**
 * 先生の疑いを進めて、ようすを決める。毎フレーム呼ぶ。
 *
 * 板書中 → （疑いが hintAt）→ ん？ → （疑いが100）→ ふりむく
 *                            ↘ （疑いが下がる）→ 気のせいか…
 */
function teacherStep(dt: number): void {
  const t = now();

  // 振り向いているあいだ。ここで食べ始めたらもちろん見つかる
  if (teacher === 'look') {
    if (eating) return finish('seen');
    if (t >= lookUntil) {
      teacher = 'board';
      // ひととおり見て何も無かったので、疑いは晴れる
      suspect = 0;
      committed = false;
      hintAt = rand(HINT_AT[0], HINT_AT[1]);
      reliefUntil = t + RELIEF_MS;
    }
    return;
  }

  if (!frozen && !committed) {
    if (eating) {
      // 音の大きさぶん ＋ 机の上の目立ちぶん
      suspect += ((noise / 100) * SUSPECT_GAIN + (food.vis / 10) * VIS_GAIN) * dt;
    } else {
      // 手を止めた。「ん？」のあいだはとくに速く下がる
      suspect -= (teacher === 'hint' ? HINT_COOL : SUSPECT_COOL) * dt;
    }
    suspect = Math.max(0, Math.min(100, suspect));
  }

  if (teacher === 'board' && suspect >= hintAt) {
    teacher = 'hint';
    hintAt0 = t;
    reliefUntil = 0;
  }

  if (teacher === 'hint' && !committed) {
    // 手を止めて疑いが下がりきった → 気のせいか…
    if (suspect <= hintAt - HINT_RELEASE) {
      teacher = 'board';
      // **ちゃんと落とす。** 落とさないと、次の口でまたすぐ「ん？」になって、
      // 大きい食材がひと口も食べられないまま終わる
      suspect = Math.min(suspect, hintAt * 0.35);
      reliefUntil = t + RELIEF_MS;
      return;
    }
    // **猶予のあいだも食べ続けていたら、先生は振り向くと決める。**
    // 止めていれば、どれだけ疑われていても決められない
    if (eating && t - hintAt0 >= GRACE_MS) {
      committed = true;
      turnAt = t + TURN_DELAY;
    }
  }

  // 決めたら、一拍おいて振り向く。この一拍で「隠しきるまでの時間」が効く
  if (committed && t >= turnAt) {
    teacher = 'look';
    lookUntil = t + rand(LOOK_MS[0], LOOK_MS[1]);
    judge();
  }
}

/** 振り向いた瞬間の判定 */
function judge(): void {
  if (screen !== 'play') return;
  // 音を鳴らしきっていたら、そっちのせいにする（モックの「からあげの音でバレた」）
  if (eating) finish(noise >= 85 ? 'noise' : 'seen');
  // 手は止めたが、まだ隠しきれていない
  else if (now() < hideUntil) finish('slow');
}

function startHold(): void {
  if (screen !== 'play' || eating) return;
  eating = true;
  hideUntil = 0;
  renderPlay();
}

function endHold(): void {
  if (!eating) return;
  eating = false;
  // 手を止めても、しまうのに少しかかる。うまいものほど長い
  hideUntil = now() + hideOf(food);
  renderPlay();
}

/** 毎フレーム。ひと口の進み、ゲージの上下、残り時間 */
function tick(): void {
  requestAnimationFrame(tick);
  if (screen !== 'play') return;
  const t = now();
  const dt = Math.min(0.1, (t - lastT) / 1000);
  lastT = t;

  // ひと口ゲージ。満タンになったら その ひと口を食べきって、グラムが増える
  const rate = 1000 / food.ms;
  if (eating) {
    bite += rate * dt;
    noise = Math.min(100, noise + noiseOf(food) * dt);
    if (bite >= 1) {
      eaten += food.g;
      bite -= 1;
    }
  } else {
    bite = Math.max(0, bite - rate * BITE_DRAIN * dt);
    noise = Math.max(0, noise - COOL_NOISE * dt);
    spiked = false;
  }

  // 音が振り切れた瞬間の「ガリッ」。疑いが一気に上がる（1回だけ）
  if (noise >= 100 && !spiked) {
    spiked = true;
    if (!frozen) suspect = Math.min(100, suspect + SUSPECT_SPIKE);
  }
  teacherStep(dt);
  if (screen !== 'play') return;
  if (t >= endsAt) return finish(null);
  renderPlay();
}

function start(): void {
  eaten = 0;
  bite = 0;
  noise = 0;
  hideUntil = 0;
  suspect = 0;
  spiked = false;
  committed = false;
  turnAt = 0;
  eating = false;
  teacher = 'board';
  hintAt = rand(HINT_AT[0], HINT_AT[1]);
  hintAt0 = 0;
  lookUntil = 0;
  reliefUntil = 0;
  endsAt = now() + ROUND * 1000;
  lastT = now();
  show('play');
  renderPlay();
}

const REASON: Record<Exclude<Caught, null>, string> = {
  noise: 'の音でバレた',
  slow: 'を隠しきれなかった',
  seen: 'を食べてるところを見られた',
};

function finish(why: Caught): void {
  if (screen !== 'play') return;
  eating = false;
  show('done');
  game.dataset['caught'] = why ? 'yes' : 'no';
  const isBest = eaten > best;
  best = Math.max(best, eaten);
  try {
    localStorage.setItem(BEST_KEY, String(best));
  } catch {
    // 保存できなくても遊べる
  }
  $('doneWhy').textContent = why ? `${food.name}${REASON[why]}` : '時間切れ。逃げ切った！';
  $('doneScore').textContent = String(eaten);
  $('doneBest').textContent = `自己ベスト ${best}g`;
  $('doneRank').textContent = rankOf(eaten);
  $('doneNew').hidden = !isBest || eaten === 0;
  shareText =
    `早弁先生で ${eaten}g（${rankOf(eaten)}）\n` +
    `${why ? `${food.name}${REASON[why]}…` : `${food.name}で逃げ切り！`}\n` +
    `授業中、先生が振り向くまでに どれだけ食べられる？\nhttps://mojitower.abarabob.com/hayaben`;
  $<HTMLAnchorElement>('share').href = `https://x.com/intent/post?text=${encodeURIComponent(shareText)}`;
}

/* ============================ 操作 ============================ */

try {
  best = Number(localStorage.getItem(BEST_KEY)) || 0;
} catch {
  best = 0;
}

$('pickList').addEventListener('click', (e) => {
  const b = (e.target as HTMLElement).closest<HTMLButtonElement>('button[data-id]');
  if (!b) return;
  const f = FOODS.find((x) => x.id === b.dataset['id']);
  if (f) {
    food = f;
    setFoodImages();
    renderPick();
  }
});
$('startBtn').addEventListener('click', () => start());
// 早弁中に食材を選びなおす。記録は付けない（途中でやめただけ）
$('backBtn').addEventListener('click', () => {
  if (screen !== 'play') return;
  endHold();
  show('pick');
  renderPick();
});
$('again').addEventListener('click', () => {
  show('pick');
  renderPick();
});

// 長押しで食べる。指を離したらやめる
const eatBtn = $('eatBtn');
eatBtn.addEventListener('pointerdown', (e) => {
  e.preventDefault();
  eatBtn.setPointerCapture(e.pointerId);
  startHold();
});
for (const ev of ['pointerup', 'pointercancel', 'pointerleave'] as const) {
  eatBtn.addEventListener(ev, () => endHold());
}
// 画面から離れたら、押しっぱなしにしない
window.addEventListener('blur', () => endHold());
document.addEventListener('visibilitychange', () => {
  if (document.hidden) endHold();
});
// キーボードでも遊べるように
eatBtn.addEventListener('keydown', (e) => {
  if (e.key === ' ' || e.key === 'Enter') {
    e.preventDefault();
    startHold();
  }
});
eatBtn.addEventListener('keyup', () => endHold());

$('share').addEventListener('click', (e) => {
  const url = $<HTMLAnchorElement>('share').href;
  if (openXPostOnWeb(shareText, url)) e.preventDefault();
});

// テストから使う窓口
(window as unknown as { __hayaben: unknown }).__hayaben = {
  /** 疑いがたまらないようにする。運に左右されずに確かめるため */
  freeze(): void {
    frozen = true;
    suspect = 0;
    teacher = 'board';
    renderPlay();
  },
  /** 疑いを直に入れる（テスト用） */
  setSuspect(v: number): void {
    suspect = v;
  },
  /** 残り時間を縮める（時間切れを試す） */
  setLeft(sec: number): void {
    endsAt = now() + sec * 1000;
  },
  hold: startHold,
  release: endHold,
  get state() {
    return {
      screen,
      food: food.id,
      eaten,
      bite,
      noise,
      suspect,
      teacher,
      eating,
      committed,
      best,
      hintAt,
    };
  },
};

setFoodImages();
renderPick();
show('pick');
tick();
